// Utilities for resolving event flyer/video media URLs.
// Supports legacy formats (filename-only) and newer formats (events/<id>/<name> or full https URL).

import { supabase } from "./supabaseClient";

const BUCKET = "event-flyers";
const DEFAULT_FOLDER = "events";

export function isVideoPath(urlOrPath = "") {
  const s = String(urlOrPath || "").toLowerCase();
  return (
    s.endsWith(".mp4") ||
    s.endsWith(".webm") ||
    s.endsWith(".mov") ||
    s.endsWith(".m4v") ||
    s.endsWith(".ogg") ||
    s.includes("youtube.com") ||
    s.includes("youtu.be") ||
    s.includes("vimeo.com")
  );
}

// Alias expected by HomeEventsCarousel / EventsSection
export const isVideoUrl = isVideoPath;

export function normalizeMediaList(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);

  if (typeof value === "string") {
    const t = value.trim();
    if ((t.startsWith("[") && t.endsWith("]")) || (t.startsWith("{") && t.endsWith("}"))) {
      try {
        const parsed = JSON.parse(t);
        return normalizeMediaList(parsed);
      } catch {
        // fall through
      }
    }
    return [value];
  }

  return [value];
}

export function toStoragePath(maybePath = "") {
  const s = String(maybePath || "").trim();
  if (!s) return "";
  if (/^https?:\/\//i.test(s) || s.startsWith("data:") || s.startsWith("blob:")) return s;
  if (s.includes("/")) return s;
  return DEFAULT_FOLDER + "/" + s;
}

export function toPublicUrl(pathOrUrl = "") {
  const s = String(pathOrUrl || "").trim();
  if (!s) return "";
  if (/^https?:\/\//i.test(s) || s.startsWith("data:") || s.startsWith("blob:")) return s;

  const storagePath = toStoragePath(s);
  const { data } = supabase.storage.from(BUCKET).getPublicUrl(storagePath);
  return (data && data.publicUrl) ? data.publicUrl : "";
}

// Main resolver expected by components
export async function resolveEventMediaUrl(raw, eventId = "") {
  const s = String(raw || "").trim();
  if (!s) return "";

  // already an absolute URL
  if (/^https?:\/\//i.test(s) || s.startsWith("data:") || s.startsWith("blob:")) return s;

  // already a storage path like events/<id>/file.jpg
  if (s.includes("/")) return toPublicUrl(s);

  // filename-only legacy: try events/<eventId>/<file> first, then events/<file>
  const id = String(eventId || "").trim();
  if (id) {
    const u1 = toPublicUrl(DEFAULT_FOLDER + "/" + id + "/" + s);
    if (u1) return u1;
  }

  return toPublicUrl(DEFAULT_FOLDER + "/" + s);
}

export default {
  BUCKET,
  DEFAULT_FOLDER,
  isVideoPath,
  isVideoUrl,
  normalizeMediaList,
  toStoragePath,
  toPublicUrl,
  resolveEventMediaUrl,
};
